import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Title2,
  Body1,
  Body1Strong,
  Card,
  CardHeader,
  CardPreview,
  Spinner,
  makeStyles,
  tokens
} from "@fluentui/react-components";

interface DashboardCard {
  clientId: string;
  clientDisplayName: string;
  dashboardId: string;
  dashboardDisplayName: string;
  description: string | null;
}

interface DashboardsResponse {
  isInternal: boolean;
  dashboards: DashboardCard[];
}

const useStyles = makeStyles({
  groupHeader: {
    marginTop: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalM
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: tokens.spacingHorizontalL
  },
  cardLink: {
    textDecoration: "none",
    color: "inherit"
  },
  empty: {
    marginTop: tokens.spacingVerticalXL,
    padding: tokens.spacingVerticalXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3
  }
});

export default function Landing() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<DashboardsResponse | null | "loading" | "error">("loading");

  useEffect(() => {
    fetch("/api/dashboards")
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as DashboardsResponse;
      })
      .then((d) => setData(d))
      .catch(() => setData("error"));
  }, []);

  // A client with exactly one dashboard goes straight to it — no point making
  // them click a single card. Internal users (who see many) never redirect.
  // The viewer's "← Dashboards" link passes { showList: true } so an explicit
  // return to this page shows the (one-card) list instead of looping back.
  const skipRedirect = !!(location.state as { showList?: boolean } | null)?.showList;
  const willRedirect =
    data !== "loading" &&
    data !== "error" &&
    data !== null &&
    !data.isInternal &&
    data.dashboards.length === 1 &&
    !skipRedirect;

  useEffect(() => {
    // willRedirect already guarantees `data` is a loaded DashboardsResponse
    // with exactly one dashboard (TS narrows via the aliased condition above).
    if (!willRedirect) return;
    const only = data.dashboards[0]!;
    navigate(`/dashboard/${only.clientId}/${only.dashboardId}`, { replace: true });
  }, [willRedirect, data, navigate]);

  if (data === "loading" || willRedirect) {
    return <Spinner size="medium" label="Loading dashboards…" />;
  }
  if (data === "error") {
    return (
      <Body1>
        Couldn't load your dashboards. The database may be waking up — try refreshing in
        about a minute.
      </Body1>
    );
  }
  if (data === null || data.dashboards.length === 0) {
    return (
      <div className={styles.empty}>
        <Body1>You don't have access to any dashboards yet. Contact your AdQuire contact.</Body1>
      </div>
    );
  }

  const groups = groupByClient(data.dashboards);

  return (
    <div>
      {groups.map(([clientId, group]) => (
        <div key={clientId}>
          {data.isInternal && (
            <Title2 className={styles.groupHeader}>{group[0]!.clientDisplayName}</Title2>
          )}
          <div className={styles.grid}>
            {group.map((d) => (
              <Link
                key={`${d.clientId}/${d.dashboardId}`}
                to={`/dashboard/${d.clientId}/${d.dashboardId}`}
                className={styles.cardLink}
              >
                <Card>
                  <CardPreview />
                  <CardHeader
                    header={<Body1Strong>{d.dashboardDisplayName}</Body1Strong>}
                    description={<Body1>{d.description ?? ""}</Body1>}
                  />
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function groupByClient(items: DashboardCard[]): Array<[string, DashboardCard[]]> {
  const map = new Map<string, DashboardCard[]>();
  for (const item of items) {
    const arr = map.get(item.clientId);
    if (arr) arr.push(item);
    else map.set(item.clientId, [item]);
  }
  return Array.from(map.entries()).sort(([, a], [, b]) =>
    a[0]!.clientDisplayName.localeCompare(b[0]!.clientDisplayName)
  );
}
